import { AccountCapabilityFormutil } from 'components/forms/AccountCapability';
import { AssetsInfoFormutilType } from 'components/forms/AssetsInfo';
import { BeneficiaryDataItem } from 'components/forms/BeneficiaryInfo';
import { DeliveryAddressFormutil } from 'components/forms/DeliveryAddress';
import type { EnhanceProgramFormutil } from 'components/forms/EnhanceProgram';
import { HomeAddressFormutil } from 'components/forms/HomeAddress';
import type { InvestInfoFormutil } from 'components/forms/InvestInfo';
import type { InvestTargetFieldType } from 'components/forms/InvestTarget';
import { JobInfoFormutilType } from 'components/forms/JobInfo';
import { PersonalInfoFormutil } from 'components/forms/PersonalInfo';
import { RealNameInfoFormutil } from 'components/forms/RealNameInfo';
import type { SameResidenceAddrFormutil } from 'components/forms/SameResidenceAddr';
import type { TaxNumberFormutil } from 'components/forms/TaxNumber';
import type { TradeTypeFormutil } from 'components/forms/TradeType';
import { UploadExemptionFieldType } from 'components/forms/UploadExemption';
import { ModifyRejectedEnum } from 'modules/Modify';
import { FormCheckType } from 'types/common';

/* --------------- 账户模块相关的类型 --------------- */
// 开户方式, 前端临时类型, 不传给server, 用于判断开户流程
export enum OpenType {
    IRA = 'IRA',
    INDIVIDUAL = 'INDIVIDUAL',
    NONE = ''
}

// customer服务下定义的账户类型, 注意与WebUser下的作区分
export enum CustomerAccountTypeEnum {
    // customer服务中的ib, 对应的其实是fd用户, 未来可能会做升级
    IB = 'ib',
    BS = 'bs',
    ND = 'nd'
}

export enum LicenseEnum {
    MARSCO = 'Marsco',
    // 古老的牌照数据, 查询数据库, 目前仅存在4个用户, 还有一个已入金
    USTS = 'USTS'
}

// 所有新开账户里不会再存在ib了, 清算只有marsco
export enum ClearingEnum {
    MARSCO = 'marsco',
    IB = 'ib'
}

export enum CategoryEnum {
    INDIVIDUAL = 'Individual',
    IRA = 'IRA'
}

/**
 * 账户的 customerType 类型
 */
export enum CustomerTypeEnum {
    INDIVIDUAL = 0,
    IRA_ROTH = 11,
    IRA_ROLLOVER = 14,
    IRA_TRADITIONAL = 15
}

/* --------------- 基础信息模块相关的类型 --------------- */
// 枚举类型: https://apidocs.tigerfintech.com/repository/1?branch=master&type=markdown&uri=%2Fdocs%2F%E5%BC%80%E6%88%B7%E5%AD%97%E6%AE%B5%E6%9E%9A%E4%B8%BE-%E4%B8%AA%E4%BA%BA.md
export enum IdTypeEnum {
    DOMESTIC_IDS = 'domestic_ids', // 大陆身份证类型
    NATIONAL_IDS = 'national_ids', // 通用身份证类型
    PASSPORT_IDS = 'passport_ids', // 护照
    RESIDENCE_PERMIT_IDS = 'residence_permit_ids', // 居留许可证
    EMPLOYMENT_PASS_IDS = 'employment_pass_ids', // 新加坡EP证件
    SSN_IDS = 'ssn_ids', // SSN
    EMPTY = ''
}
export enum NameTypeEnum {
    COMMON2 = 'COMMON2',
    USA = 'USA',
    COMMON = 'COMMON', // 理论上美国只有USA和COMMON2, COMMON是老数据
    CHN = 'CHN' // 非SSN开户中国国籍的使用CHN姓名格式
}
export enum HomeAddressTypeEnum {
    CHN = 'CHN',
    USA = 'USA',
    COMMON = 'COMMON',
    // 加拿大使用COMMON2地址格式
    COMMON2 = 'COMMON2'
}
export enum MaritalStatusEnum {
    SINGLE = 0,
    MARRIED = 1,
    DIVORCED = 2,
    WIDOWED = 3
}
export enum GenderEnum {
    MALE = 0,
    FEMALE = 1,
    OTHER = 2
}
export enum SuffixEnum {
    None = 0,
    Jr = 1,
    Sr = 2,
    I = 3,
    II = 4,
    III = 5
}

/* --------------- 工作信息模块 --------------- */
export enum JobTypeEnum {
    Empty = -1,
    Employed = 0,
    SelfEmployed = 1,
    StudentOrIntern = 2,
    Retired = 3,
    Homemaker = 4,
    Unemployment = 5,
    AtHomeTrader = 6
}

/* --------------- 投资信息模块的类型 --------------- */
// 账户类型枚举值
export enum AccountCapabilitiesEnum {
    CASH = 'CASH', // 现金账户
    MARGIN = 'MARGIN' // 保证金账户
}

/* --------------- 受益人信息模块 --------------- */
export enum BeneficiaryRelationshipEnum {
    SPOUSE = 'Spouse',
    SON = 'Son',
    DAUGHTER = 'Daughter',
    FATHER = 'Father',
    MOTHER = 'Mother',
    BROTHER = 'Brother',
    SISTER = 'Sister',
    OTHER = 'Other'
}
// 受益人类型枚举
export enum BeneficiaryTypeEnum {
    PRIMARY = 0,
    SECONDARY = 1
}

/* --------------- 总的用户信息相关类型 --------------- */
export interface CustomerInfoType
    extends RealNameInfoFormutil,
        HomeAddressFormutil,
        SameResidenceAddrFormutil,
        DeliveryAddressFormutil,
        PersonalInfoFormutil,
        JobInfoFormutilType,
        AssetsInfoFormutilType,
        TradeTypeFormutil,
        EnhanceProgramFormutil,
        TaxNumberFormutil,
        InvestTargetFieldType,
        InvestInfoFormutil,
        AccountCapabilityFormutil,
        UploadExemptionFieldType {
    /* --------------- 账户相关字段类型 --------------- */
    category: CategoryEnum;
    customer_type: CustomerTypeEnum;
    clearing: ClearingEnum;
    license: LicenseEnum;
    account_type: CustomerAccountTypeEnum;
    // 目前美国开户source还没有明确沟通
    source?: string;

    /* --------------- 基础信息相关字段类型 --------------- */
    age: number;
    email: string;
    phone: string;
    tel_code: string;
    id_type: IdTypeEnum;
    name_type: NameTypeEnum;
    country_code: string;
    legal_residence_country: string;
    tax_residence_country: string;
    issue_country: string;

    /* --------------- 工作/规管信息相关 --------------- */
    temporary_regulatory_members_checked?: FormCheckType;
    temporary_regulatory_stockholder_checked?: FormCheckType;
    temporary_employee_track_checked?: FormCheckType;
    regulatory_relationship?: string;
    regulatory_members: string;
    regulatory_company_other?: {
        company: string;
        career: string;
    };
    regulatory_stockholder: string;

    /* --------------- 受益人信息模块 --------------- */
    temporaryPrimaryBeneficiary?: BeneficiaryDataItem[];
    temporarySecondaryBeneficiary?: BeneficiaryDataItem[];
    beneficiary_info: BeneficiaryDataItem[];
    // 其他信息
    extra?: {
        // 是否为大客户开户
        assisted_opening?: boolean;
        // 大客户是否已确认开户资料
        assisted_opening_ack?: boolean;
    };
}

export type CustomerInfoKeyType = keyof CustomerInfoType;

/* --------------- customer服务接口相关的通用类型 --------------- */
// 用户的当前的状态
export enum CustomerProcessStatusEnum {
    UNACCOUNT = 'UNACCOUNT', // 未开户
    OPENED = 'OPENED', // 已经开户成功
    FUNDED = 'FUNDED', // 已入金
    CONTINUE = 'CONTINUE', // 继续开户(缺少证件)
    INVALID = 'INVALID', // 修改资料(被打回)
    AUDITING = 'AUDITING', // 审核中(在Bos)
    FACE = 'FACE', // 人脸识别
    CLOSED = 'CLOSED', // 账户已注销
    ACTIVATING = 'ACTIVATING' // 激活中(fd账户关户后重新激活);
}

// 类型参考: https://apidocs.tigerfintech.com/repository/1/api/master/apis/web/Web%E6%8E%A5%E5%8F%A3.json#/operations/get-api-v3-individual-account-process
// 目前是仅用到ASSISTED_OPENING_ACK
export enum ProcessMissingTypeEnum {
    // 大客户开户资料确认
    ASSISTED_OPENING_ACK = 'ASSISTED_OPENING_ACK'
}

// process接口返回的数据类型
export interface AccountProcessDataType {
    missing: Array<{
        type: ProcessMissingTypeEnum;
    }>;
    // 打回项
    rejected: Array<{
        type: ModifyRejectedEnum;
        reasons: Array<{
            key: string;
            text: string;
        }>;
        field_description: Array<{
            name: keyof CustomerInfoType;
            text: string;
        }>;
    }>;
    status: {
        status: CustomerProcessStatusEnum;
    };
    is_link: boolean;
}
